import { OWN_SHIPPING_CARRIER_ID } from 'app/utils/constants/shipping.constants'
import {
  hasShipOnlyShippingType,
  isCollectShippingType
} from 'app/utils/helpers/shipping.helpers'

export type ShippingScopeAvailability =
  | 'domestic_only'
  | 'international_only'
  | 'both'
  | null

export type ListingShippingMethodType = 'own_shipping' | 'integrated'
export type ListingShippingScope = 'domestic' | 'international'

export interface ListingReturnsInfo {
  acceptReturn: boolean
  returnPaidBy: string | null
  returnDays?: number | null
  returnPolicyInfo?: string | null
}

export interface ListingResolvedReturns {
  domestic: ListingReturnsInfo | null
  international: ListingReturnsInfo | null
}

export interface ListingShippingMethodInfo {
  scope: ListingShippingScope
  method: ListingShippingMethodType
  isFree: boolean
  ownShippingPrice: number | null
}

export const getShippingConfigsAvailability = (shipping?: ShippingModel) => {
  const configs = shipping?.shipping_configs
  const hasShip = hasShipOnlyShippingType(shipping?.shipping_types)

  if (configs) {
    return {
      isDomesticEnabled: !!configs.domestic?.enabled,
      isInternationalEnabled: !!configs.international?.enabled
    }
  }

  // Legacy listings without shipping_configs:
  // international flag was additive on top of domestic shipping
  return {
    isDomesticEnabled: hasShip,
    isInternationalEnabled: !!shipping?.international_shipping
  }
}

export const getShippingScopeAvailability = ({
  shipping,
  shippingTypes
}: {
  shipping?: ShippingModel
  shippingTypes?: string[]
}): ShippingScopeAvailability => {
  if (!hasShipOnlyShippingType(shippingTypes)) return null

  const { isDomesticEnabled, isInternationalEnabled } =
    getShippingConfigsAvailability(shipping)

  if (isDomesticEnabled && isInternationalEnabled) return 'both'
  if (isInternationalEnabled) return 'international_only'
  if (isDomesticEnabled) return 'domestic_only'

  return null
}

const mapConfigReturns = (
  returns?: ShippingConfigReturns | null
): ListingReturnsInfo => ({
  acceptReturn: !!returns?.accept_return,
  returnPaidBy: returns?.return_paid_by ?? null,
  returnDays: returns?.return_days,
  returnPolicyInfo: returns?.return_policy_info
})

const mapFlatReturns = (shipping?: ShippingModel): ListingReturnsInfo => ({
  acceptReturn: !!shipping?.accept_return,
  returnPaidBy: shipping?.return_paid_by ?? null,
  returnDays: shipping?.return_days,
  returnPolicyInfo: shipping?.return_policy_info
})

export const resolveListingReturns = (
  shipping?: ShippingModel
): ListingResolvedReturns => {
  const isClickAndCollectOnly = isCollectShippingType(
    shipping?.shipping_types || []
  )

  if (isClickAndCollectOnly || !shipping?.shipping_configs) {
    return {
      domestic: mapFlatReturns(shipping),
      international: null
    }
  }

  const { domestic, international } = shipping.shipping_configs

  return {
    domestic: domestic?.enabled ? mapConfigReturns(domestic.returns) : null,
    international: international?.enabled
      ? mapConfigReturns(international.returns)
      : null
  }
}

const mapConfigShippingMethod = (
  scope: ListingShippingScope,
  config?: ShippingConfig | null
): ListingShippingMethodInfo | null => {
  if (!config?.enabled) return null

  const isOwnShipping = !!config.own_shipping?.enabled

  return {
    scope,
    method: isOwnShipping ? 'own_shipping' : 'integrated',
    isFree: !!config.make_shipping_free,
    ownShippingPrice:
      isOwnShipping && !config.make_shipping_free
        ? config.own_shipping?.shipping_price ?? null
        : null
  }
}

const mapFlatShippingMethod = (
  shipping?: ShippingModel
): ListingShippingMethodInfo | null => {
  if (!shipping) return null

  const isOwnShipping = shipping.carrier_id === OWN_SHIPPING_CARRIER_ID

  return {
    scope: 'domestic',
    method: isOwnShipping ? 'own_shipping' : 'integrated',
    isFree: !!shipping.make_shipping_free,
    ownShippingPrice:
      isOwnShipping && !shipping.make_shipping_free
        ? shipping.shipping_price ?? null
        : null
  }
}

export const resolveListingShippingMethods = (
  shipping?: ShippingModel
): ListingShippingMethodInfo[] => {
  if (!hasShipOnlyShippingType(shipping?.shipping_types)) return []

  if (!shipping?.shipping_configs) {
    const flatMethod = mapFlatShippingMethod(shipping)
    return flatMethod ? [flatMethod] : []
  }

  const { domestic, international } = shipping.shipping_configs

  return [
    mapConfigShippingMethod('domestic', domestic),
    mapConfigShippingMethod('international', international)
  ].filter((method): method is ListingShippingMethodInfo => !!method)
}
